import React, { useState, useMemo } from "react";
import { Settings, Euro } from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import { useBateaux } from "@/hooks/useBateaux";
import { useMaintenances, Maintenance } from "@/hooks/useMaintenances";
import { useProAccess } from "@/hooks/useProAccess";
import SelecteurNavire from "@/components/bateaux/SelecteurNavire";
import VueCompacteMaintenances from "@/components/maintenance/VueCompacteMaintenances";
import MaintenanceHeader from "@/components/maintenance/MaintenanceHeader";
import MaintenanceStats from "@/components/maintenance/MaintenanceStats";
import MaintenanceDialog from "@/components/maintenance/MaintenanceDialog";
import PremiumLimitBanner from "@/components/premium/PremiumLimitBanner";
import HiddenDataBanner from "@/components/premium/HiddenDataBanner";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { isBefore, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { exportMaintenancesPDF } from '@/utils/exportMaintenancesPDF';

const FREE_MAINTENANCE_LIMIT = 3;

const MaintenancePage = () => {
  const { bateaux, loading: loadingBateaux, getBateauParDefaut } = useBateaux();
  const {
    maintenances,
    loading: loadingMaintenances,
    ajouterMaintenance,
    modifierMaintenance,
    supprimerMaintenance,
    refetch,
  } = useMaintenances();
  const { isProAccount, loading: proLoading } = useProAccess();
  const [selectedBateauId, setSelectedBateauId] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingMaintenance, setEditingMaintenance] = useState<Maintenance | null>(null);
  const [selectedCostYear, setSelectedCostYear] = useState<number>(new Date().getFullYear());
  const [showCostFilter, setShowCostFilter] = useState(false);

  // Auto-sélectionner le navire par défaut
  React.useEffect(() => {
    if (!loadingBateaux && bateaux.length > 0 && !selectedBateauId) {
      const bateauParDefaut = getBateauParDefaut();
      if (bateauParDefaut) {
        setSelectedBateauId(bateauParDefaut.id);
      }
    }
  }, [bateaux, loadingBateaux, selectedBateauId, getBateauParDefaut]);

  const handleEdit = (maintenance: Maintenance) => {
    setEditingMaintenance(maintenance);
  };

  const handleDialogClose = () => {
    setIsAddDialogOpen(false);
    setEditingMaintenance(null);
  };

  // Vérification quota maintenances en compte gratuit
  const hasReachedFreeLimit = useMemo(() => {
    if (proLoading) return false; // prudence pendant le chargement
    if (isProAccount) return false;
    return maintenances.length >= FREE_MAINTENANCE_LIMIT;
  }, [proLoading, isProAccount, maintenances.length]);

  const handleAddClick = () => {
    if (hasReachedFreeLimit) {
      toast.error(`Vous avez atteint la limite de ${FREE_MAINTENANCE_LIMIT} maintenances en compte gratuit. Passez Premium pour en ajouter davantage.`);
      return;
    }
    setIsAddDialogOpen(true);
  };

  const handleExportPDF = (annee: number) => {
    const bateau = bateaux.find(b => b.id === selectedBateauId);
    const source = selectedBateauId
      ? maintenances.filter(m => m.bateau_id === selectedBateauId)
      : maintenances;
    const completed = source.filter(m => {
      const y = new Date(m.date_echeance).getFullYear();
      return m.statut === 'Terminée' && y === annee;
    });
    if (completed.length === 0) {
      toast.error(`Aucune maintenance effectuée en ${annee}.`);
      return;
    }
    exportMaintenancesPDF({ maintenances: completed, annee, bateauNom: bateau?.nom });
    toast.success(`PDF généré (${completed.length} maintenance${completed.length > 1 ? 's' : ''})`);
  };

  if (loadingBateaux || loadingMaintenances) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center p-6 bg-white rounded-lg shadow-sm">
            <Settings className="mx-auto h-12 w-12 text-nautic animate-pulse" />
            <h3 className="mt-2 text-xl font-semibold text-nautic">Chargement...</h3>
            <p className="mt-1 text-gray-500">
              Récupération des données de maintenance...
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Pour les comptes gratuits, masquer les maintenances au-delà de la limite
  const allMaintenancesFiltrees = selectedBateauId 
    ? maintenances.filter(m => m.bateau_id === selectedBateauId)
    : maintenances;

  const hiddenMaintenancesCount = !isProAccount && allMaintenancesFiltrees.length > FREE_MAINTENANCE_LIMIT
    ? allMaintenancesFiltrees.length - FREE_MAINTENANCE_LIMIT
    : 0;

  const maintenancesFiltrees = !isProAccount 
    ? allMaintenancesFiltrees.slice(0, FREE_MAINTENANCE_LIMIT)
    : allMaintenancesFiltrees;

  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 7 }, (_, i) => currentYear + 1 - i);

  // Calculer le coût total des maintenances de l'année sélectionnée
  const coutMaintenancesAnnee = maintenancesFiltrees
    .filter(m => {
      const maintenanceYear = new Date(m.date_echeance).getFullYear();
      return maintenanceYear === selectedCostYear && m.prix;
    })
    .reduce((total, m) => total + (m.prix || 0), 0);

  // Nombre de maintenances comptabilisées
  const nombreMaintenancesAnnee = maintenancesFiltrees.filter(m => {
    const maintenanceYear = new Date(m.date_echeance).getFullYear();
    return maintenanceYear === selectedCostYear && m.prix;
  }).length;

  const today = new Date();

  // Fonction pour vérifier si une maintenance est en retard
  const isMaintenanceOverdue = (maintenance: Maintenance) => {
    const dueDate = parseISO(maintenance.date_echeance);
    return isBefore(dueDate, today) && maintenance.statut !== 'Terminée';
  };

  // Maintenances en retard
  const maintenancesEnRetard = maintenancesFiltrees.filter(isMaintenanceOverdue);

  // Maintenances EN COURS (non en retard)
  const maintenancesEnCours = maintenancesFiltrees.filter(m => 
    m.statut === 'En cours' && !isMaintenanceOverdue(m)
  );

  // Maintenances EN ATTENTE (non en retard)
  const maintenancesEnAttente = maintenancesFiltrees.filter(m => 
    m.statut === 'En attente' && !isMaintenanceOverdue(m)
  );
  
  const maintenancesTerminees = maintenancesFiltrees.filter(m => m.statut === 'Terminée');

  return (
    <MainLayout>
      <div className="space-y-6 p-4 lg:p-0">
        <MaintenanceHeader 
          isProAccount={isProAccount}
          onAddClick={handleAddClick}
          disableAdd={hasReachedFreeLimit}
          onExportPDF={handleExportPDF}
        />

        {/* Bannière limite atteinte */}
        {hasReachedFreeLimit && (
          <PremiumLimitBanner
            title="Limite atteinte"
            description={`Vous avez atteint la limite de ${FREE_MAINTENANCE_LIMIT} maintenances en compte gratuit.`}
          />
        )}

        {/* Bannière données masquées */}
        {hiddenMaintenancesCount > 0 && (
          <HiddenDataBanner hiddenCount={hiddenMaintenancesCount} itemLabel="maintenance" />
        )}

        {/* Sélecteur de navire */}
        <SelecteurNavire 
          bateaux={bateaux}
          selectedBateauId={selectedBateauId}
          onBateauChange={setSelectedBateauId}
        />

        {/* Statistiques */}
        <MaintenanceStats
          maintenancesEnRetard={maintenancesEnRetard.length}
          maintenancesEnCours={maintenancesEnCours.length}
          maintenancesEnAttente={maintenancesEnAttente.length}
          maintenancesTerminees={maintenancesTerminees.length}
        />

        {/* Coût des maintenances de l'année */}
        <Card 
          className={`border-0 shadow-sm bg-gradient-to-r from-nautic/5 to-nautic-gold/10 cursor-pointer transition-all ${showCostFilter ? 'ring-2 ring-nautic' : 'hover:shadow-md'}`}
          onClick={() => setShowCostFilter(prev => !prev)}
        >
          <CardContent className="p-4 lg:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-nautic-gold/20 rounded-lg">
                  <Euro className="h-5 w-5 text-nautic" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-muted-foreground">Coût des maintenances</p>
                    <div onClick={(e) => e.stopPropagation()}>
                      <Select 
                        value={selectedCostYear.toString()} 
                        onValueChange={(value) => setSelectedCostYear(parseInt(value))}
                      >
                        <SelectTrigger className="w-24 h-7 text-sm bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableYears.map(year => (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-nautic">
                    {coutMaintenancesAnnee.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {nombreMaintenancesAnnee} maintenance{nombreMaintenancesAnnee > 1 ? 's' : ''} avec coût renseigné
                    {showCostFilter ? ' — Cliquez pour voir toutes' : ' — Cliquez pour filtrer'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vue compacte des maintenances */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-nautic">Mes maintenances</h2>
          <VueCompacteMaintenances
            bateauId={selectedBateauId || undefined}
            onEditMaintenance={handleEdit}
            maintenances={maintenancesFiltrees}
            supprimerMaintenance={supprimerMaintenance}
            modifierMaintenance={modifierMaintenance}
            refetch={refetch}
            costFilterYear={showCostFilter ? selectedCostYear : undefined}
          />
        </div>

        <MaintenanceDialog
          isOpen={isAddDialogOpen || !!editingMaintenance}
          onClose={handleDialogClose}
          editingMaintenance={editingMaintenance}
          ajouterMaintenance={ajouterMaintenance}
          modifierMaintenance={modifierMaintenance}
          onSaved={refetch}
        />
      </div>
    </MainLayout>
  );
};

export default MaintenancePage;
